import { use, useEffect, useMemo, useState } from 'react';
import { jsonParseWithLowerCaseKeys } from 'shared/utils/string-utils';

import BaseAlarmOnlyScenarioConfiguration from 'features/residents/models/configurations/base-alarm-only-scenario-configuration';
import TimeFrame from 'features/residents/models/configurations/time-frame';
import OrganisationUnitScenario from 'features/residents/models/organisation-unit-scenario';
import ResidentSettingsProviderContext from 'features/residents/resident-settings/providers/contexts/resident-settings-provider-context';

type AlarmOnlyScenarioConfiguration = {
  configuration: BaseAlarmOnlyScenarioConfiguration;
  timeFrameValuesChanged: (newTimeFrames: TimeFrame[] | null, newActiveAllDay: boolean) => void;
};

export const useAlarmOnlyScenarioConfiguration = (
  index: number,
  organisationUnitScenario?: OrganisationUnitScenario,
): AlarmOnlyScenarioConfiguration => {
  const {
    detailEditState: { viewingMode, currentResidentSetting, residentSettingEditForm },
  } = use(ResidentSettingsProviderContext);

  const rawConfiguration = useMemo(
    () => currentResidentSetting?.organisationUnitScenarios[index].configuration,
    [currentResidentSetting, index],
  );

  const [configuration, setConfiguration] = useState(jsonParseWithLowerCaseKeys(rawConfiguration) as BaseAlarmOnlyScenarioConfiguration);

  const scenarioConfigurationUpdated = (newConfiguration: unknown) => {
    residentSettingEditForm.setValue(`organisationUnitScenarios.${index}.configuration`, JSON.stringify(newConfiguration), {
      shouldDirty: true,
    });
  };

  const timeFrameValuesChanged = (newTimeFrames: TimeFrame[] | null, newActiveAllDay: boolean): void => {
    const newConfig = {
      ...configuration,
      timeFrames: newTimeFrames,
      sendAlarmAllDay: newActiveAllDay,
    };
    scenarioConfigurationUpdated(newConfig);
    setConfiguration(newConfig);
  };

  useEffect(() => {
    residentSettingEditForm.clearErrors(`organisationUnitScenarios.${index}`);
    setConfiguration(jsonParseWithLowerCaseKeys(rawConfiguration) as BaseAlarmOnlyScenarioConfiguration);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawConfiguration, viewingMode]);

  return {
    configuration,
    timeFrameValuesChanged,
  };
};
